import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import ReportBuilder from '../components/reports/ReportBuilder';
import ReportViewer from '../components/reports/ReportViewer';
import SavedReports from '../components/reports/SavedReports';
import '../styles/Reports.css';

const Reports = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('builder');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [actionReports, setActionReports] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [statusConfig, setStatusConfig] = useState({
    conferenceStatuses: [],
    processStages: []
  });

  useEffect(() => {
    fetchBaseData();
    fetchSavedReports();
  }, []);

  const fetchBaseData = async () => {
    try {
      setLoading(true);
      
      // Fetch candidates
      const candidatesRef = collection(db, 'candidates');
      const candidatesSnapshot = await getDocs(candidatesRef);
      const candidatesData = candidatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCandidates(candidatesData);

      // Fetch action reports
      const reportsRef = collection(db, 'actionReports');
      const reportsSnapshot = await getDocs(reportsRef);
      const reportsData = reportsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setActionReports(reportsData);

      // Fetch districts
      const districtsRef = collection(db, 'districts');
      const districtsSnapshot = await getDocs(districtsRef);
      const districtsData = districtsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDistricts(districtsData);

      // Fetch status configurations
      const statusConfigDoc = await getDoc(doc(db, 'systemConfig', 'statusOptions'));
      if (statusConfigDoc.exists()) {
        setStatusConfig(statusConfigDoc.data());
      } else {
        // Set default status config if none exists
        setStatusConfig({
          conferenceStatuses: [
            { id: 'lay', label: 'Lay', enabled: true, color: '#17a2b8' },
            { id: 'clm', label: 'Certified Lay Ministry', enabled: true, color: '#ffc107' },
            { id: 'cmc', label: 'Certified Ministry Candidate', enabled: true, color: '#fd7e14' },
            { id: 'supply-pastor', label: 'Supply Pastor', enabled: true, color: '#007bff' },
            { id: 'local-pastor', label: 'Local Pastor', enabled: true, color: '#28a745' },
            { id: 'other-fellowship', label: 'Other Fellowship', enabled: true, color: '#6f42c1' },
            { id: 'other', label: 'Other', enabled: true, color: '#6c757d' }
          ],
          processStages: [
            { id: 'inquiring', label: 'Inquiring', enabled: true, color: '#17a2b8', archived: false },
            { id: 'certified', label: 'Certified', enabled: true, color: '#ffc107', archived: false },
            { id: 'full-time', label: 'Full-Time', enabled: true, color: '#28a745', archived: false },
            { id: 'part-time', label: 'Part-Time', enabled: true, color: '#fd7e14', archived: false },
            { id: 'completed', label: 'Completed', enabled: true, color: '#6f42c1', archived: false },
            { id: 'discontinued', label: 'Discontinued', enabled: true, color: '#dc3545', archived: false },
            { id: 'archived', label: 'Archived', enabled: true, color: '#6c757d', archived: true }
          ]
        });
      }

    } catch (error) {
      console.error('Error fetching base data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedReports = async () => {
    try {
      const reportsRef = collection(db, 'savedReports');
      const q = query(reportsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const reports = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSavedReports(reports);
    } catch (error) {
      console.error('Error fetching saved reports:', error);
    }
  };

  const handleGenerateReport = (reportConfig) => {
    setLoading(true);
    try {
      const generatedData = generateReportData(reportConfig);
      setReportData({
        config: reportConfig,
        data: generatedData,
        generatedAt: new Date()
      });
      setActiveTab('viewer');
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateReportData = (config) => {
    let data = [];
    
    // Start with the selected data source
    switch (config.dataSource) {
      case 'candidates':
        data = [...candidates];
        break;
      case 'actionReports':
        data = [...actionReports];
        break;
      case 'courseOfStudy':
        data = candidates.map(candidate => ({
          ...candidate,
          courseOfStudy: candidate.courseOfStudy || {}
        }));
        break;
      case 'combined':
        // Combine candidates with their action reports
        data = candidates.map(candidate => {
          const candidateReports = actionReports.filter(report => 
            report.candidateId === candidate.id
          );
          return {
            ...candidate,
            actionReports: candidateReports,
            totalReports: candidateReports.length,
            latestReport: candidateReports.length > 0 ? 
              candidateReports.sort((a, b) => new Date(b.createdAt?.toDate?.() || b.createdAt) - new Date(a.createdAt?.toDate?.() || a.createdAt))[0] : null
          };
        });
        break;
      default:
        data = [...candidates];
    }

    // Apply filters
    if (config.filters && config.filters.length > 0) {
      config.filters.forEach(filter => {
        data = applyFilter(data, filter);
      });
    }

    // Apply sorting
    if (config.sorting && config.sorting.field) {
      data = applySorting(data, config.sorting);
    }

    // Apply grouping
    if (config.groupBy) {
      data = applyGrouping(data, config.groupBy);
    }

    return data;
  };

  const applyFilter = (data, filter) => {
    return data.filter(item => {
      const value = getNestedValue(item, filter.field);
      
      switch (filter.operator) {
        case 'equals':
          return value === filter.value;
        case 'contains':
          return value && value.toString().toLowerCase().includes(filter.value.toLowerCase());
        case 'startsWith':
          return value && value.toString().toLowerCase().startsWith(filter.value.toLowerCase());
        case 'greaterThan':
          return value > filter.value;
        case 'lessThan':
          return value < filter.value;
        case 'between':
          return value >= filter.value[0] && value <= filter.value[1];
        case 'in':
          return filter.value.includes(value);
        case 'notEmpty':
          return value !== null && value !== undefined && value !== '';
        case 'isEmpty':
          return value === null || value === undefined || value === '';
        default:
          return true;
      }
    });
  };

  const applySorting = (data, sorting) => {
    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, sorting.field);
      const bValue = getNestedValue(b, sorting.field);
      
      if (aValue < bValue) return sorting.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const applyGrouping = (data, groupBy) => {
    const grouped = {};
    
    data.forEach(item => {
      const groupValue = getNestedValue(item, groupBy) || 'Ungrouped';
      if (!grouped[groupValue]) {
        grouped[groupValue] = [];
      }
      grouped[groupValue].push(item);
    });
    
    return grouped;
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
      if (current && typeof current === 'object') {
        return current[key];
      }
      return undefined;
    }, obj);
  };

  const handleLoadSavedReport = async (reportId) => {
    try {
      const reportDoc = await getDoc(doc(db, 'savedReports', reportId));
      if (reportDoc.exists()) {
        const savedReport = reportDoc.data();
        handleGenerateReport(savedReport.config);
      }
    } catch (error) {
      console.error('Error loading saved report:', error);
    }
  };

  if (loading && activeTab === 'builder') {
    return (
      <div className="reports-container">
        <div className="loading-spinner">Loading reports data...</div>
      </div>
    );
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1>Advanced Reports</h1>
        <div className="reports-tabs">
          <button 
            className={`tab-btn ${activeTab === 'builder' ? 'active' : ''}`}
            onClick={() => setActiveTab('builder')}
          >
            Report Builder
          </button>
          <button 
            className={`tab-btn ${activeTab === 'viewer' ? 'active' : ''}`}
            onClick={() => setActiveTab('viewer')}
            disabled={!reportData}
          >
            Report Viewer
          </button>
          <button 
            className={`tab-btn ${activeTab === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveTab('saved')}
          >
            Saved Reports
          </button>
        </div>
      </div>

      <div className="reports-content">
        {activeTab === 'builder' && (
          <ReportBuilder
            onGenerateReport={handleGenerateReport}
            candidates={candidates}
            actionReports={actionReports}
            districts={districts}
            loading={loading}
          />
        )}

        {activeTab === 'viewer' && reportData && (
          <ReportViewer
            reportData={reportData}
            onSaveReport={fetchSavedReports}
            currentUser={currentUser}
            statusConfig={statusConfig}
            districts={districts}
          />
        )}

        {activeTab === 'saved' && (
          <SavedReports
            savedReports={savedReports}
            onLoadReport={handleLoadSavedReport}
            onRefresh={fetchSavedReports}
            currentUser={currentUser}
          />
        )}
      </div>
    </div>
  );
};

export default Reports;
